import React, { useState, useEffect, useRef } from 'react';
import { Button, Row, Col, Form } from 'react-bootstrap';
import { FaFileExcel } from 'react-icons/fa';
import Select from 'react-select';
import { toast } from 'react-toastify';

import Tabela from '~/Components/TabelaDinamica/Tabela';
import ModalConfirmacao from '~/Components/ModalConfirmacao';
import { customStyles, DropdownIndicator } from '~/Styles/reactSelectStyles';
import {
  atualizarQualidadeEmMassa,
  importarQualidades,
  exportarQualidades,
  getQualidadesEmpreendimento,
} from '~/Services/Service';

const TabQualidade = ({ ativos, familia, onRecarregar }) => {
  const [qualidades, setQualidades] = useState([]);
  const [qualidadeSelecionada, setQualidadeSelecionada] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [aplicando, setAplicando] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (familia) {
      getQualidadesEmpreendimento(familia)
        .then((res) => setQualidades(res.data || []))
        .catch(() => {});
    }
  }, [familia]);

  const opcoesQualidade = qualidades.map((q) => ({
    value: q.codigo_qualidade,
    label: q.nome_qualidade,
  }));

  const handleAplicar = () => {
    if (!qualidadeSelecionada) {
      toast.error('Selecione uma qualidade');
      return;
    }
    setShowConfirm(true);
  };

  const confirmarAplicacao = async (confirmado) => {
    setShowConfirm(false);
    if (!confirmado) return;
    setAplicando(true);
    try {
      const res = await atualizarQualidadeEmMassa({
        familia,
        codigo_qualidade: qualidadeSelecionada.value,
      });
      toast.success(
        `Qualidade aplicada a ${(res.data && res.data.atualizados) || 0} ativo(s)`
      );
      setQualidadeSelecionada(null);
      if (onRecarregar) onRecarregar();
    } catch (err) {
      const msg =
        (err.response && err.response.data && err.response.data.message) ||
        (err.response && err.response.data) ||
        'Erro ao aplicar qualidade';
      toast.error(msg);
    } finally {
      setAplicando(false);
    }
  };

  const handleExportar = async () => {
    try {
      const res = await exportarQualidades(familia);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'qualidades.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Erro ao exportar qualidades');
    }
  };

  const handleImportar = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('familia', familia);
    try {
      const res = await importarQualidades(formData);
      toast.success(`${(res.data && res.data.importados) || 0} qualidades importadas`);
      if (onRecarregar) onRecarregar();
    } catch (err) {
      const msg =
        (err.response && err.response.data && err.response.data.message) ||
        (err.response && err.response.data) ||
        'Erro ao importar qualidades';
      toast.error(msg);
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const colunas = [
    { title: 'Código', key: 'codigo_ativo', width: '80px' },
    { title: 'Nome', key: 'nome' },
    { title: 'Quadra', key: 'quadra', width: '80px' },
    { title: 'Lote', key: 'lote', width: '80px' },
    { title: 'Qualidade', key: 'nome_qualidade', width: '120px' },
    {
      title: 'Área',
      key: 'area_total',
      width: '80px',
      render: (v) =>
        v != null
          ? v.toLocaleString('pt-BR', { minimumFractionDigits: 2 })
          : '',
    },
  ];

  return (
    <>
      <Row className="mb-3">
        <Col md={4}>
          <Form.Label>Nova Qualidade</Form.Label>
          <Select
            value={qualidadeSelecionada}
            onChange={setQualidadeSelecionada}
            options={opcoesQualidade}
            placeholder="Selecione..."
            styles={customStyles}
            components={{ DropdownIndicator }}
            isClearable
          />
        </Col>
        <Col className="d-flex align-items-end justify-content-end">
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            style={{ display: 'none' }}
            onChange={handleImportar}
          />
          <Button
            variant="outline-success"
            size="sm"
            className="mr-2"
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
          >
            <FaFileExcel className="mr-1" /> Importar
          </Button>
          <Button
            variant="outline-success"
            size="sm"
            className="mr-2"
            onClick={handleExportar}
          >
            <FaFileExcel className="mr-1" /> Exportar
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleAplicar}
            disabled={aplicando || !qualidadeSelecionada}
          >
            {aplicando ? 'Aplicando...' : 'Aplicar'}
          </Button>
        </Col>
      </Row>

      <Tabela colunas={colunas} dados={ativos} />

      <ModalConfirmacao
        show={showConfirm}
        onClose={confirmarAplicacao}
        title="Aplicar Qualidade"
        message={`Aplicar qualidade "${qualidadeSelecionada && qualidadeSelecionada.label}" a ${ativos.length} ativo(s)?`}
      />
    </>
  );
};

export default TabQualidade;
